// Nomad-mon Go! — v11 COLLECTION: DUOS + FUSIONS tabs, forge sheets.
//
// The Collection (binder) grows a three-way tab: Cards · Duos · Fusions.
//  DUOS     owned / ready Duos show the living art (A<->B cycle), ★, PWR; a locked
//           Duo (not ready) is a "?" card like a locked Fusion. Every tile shows the
//           two required cards with owned ✓ / missing. Tap -> sheet -> Forge
//           -> confirm -> the server spends both and mints the Duo.
//  FUSIONS  a locked Fusion is a dark "?" card whose RECIPE is visible as
//           requirement chips with live progress. Ready -> sheet lists exactly
//           which cards will be spent (cheapest qualifying cards picked for
//           you; tap one to swap it) -> Forge -> the server validates,
//           spends and mints.
// The minted card starts at the AVERAGE level of what was spent and then
// levels from wins like any card. Server = authority (identity.forge).
import { recipe, duos, fusions, reagentEligible, reqTerms, checkReq, autoSelect, reagentValue, mintXp, spendableCopies, fightersAfter, planFusion, squadTooSmall, MIN_SQUAD_FIGHTERS } from './forged.js';
import { ensureForgedFrameCss, idleOffscreenFrames } from './forged-frame.js';
import { forgedLayersHtml, wireForgedVideos, attachForgedFx, detachForgedFx, duoLensTilt, duoLensSweep, duoLensFollow, duoLensRelease, duoLensActive, lensGyroLive, TILT_SVG, DRAG_SVG } from './forged-card.js';
import { mountDuoGallery, SHOWCASE_DUOS } from './duo-gallery.js';
import { runForgeCeremony } from './forge-ceremony.js';
import { FORGE_SFX, ANVIL_SFX, buildChargeSfx } from './forge-sfx.js';
import { levelFromXp, MAX_XP, effectivePwr } from './levelling.js';
import * as nav from './nav.js';

const CSS = `
#binder.forge-on #binderGrid,#binder.forge-on #binderTools{display:none!important}
#forgePanel{display:none;flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:6px 16px 40px;position:relative;z-index:2;
  overscroll-behavior:contain;touch-action:pan-y}
#binder.forge-on:not(.view-car) #forgePanel{display:block}
#forgePanel .fgHero{margin:4px 0 22px}
#forgePanel .fgHero .dg-slot{--dg-h:min(56svh,560px,calc((100vw - 32px) * 1.397))}
#forgePanel .fgHero .dg-info>b{font-size:20px;letter-spacing:-.01em}
#forgePanel .fgHero .dg-info .fgSub{font-size:15px}
#forgePanel .fgHero .fgSrc{justify-content:center;width:min(100%,420px)}
#forgePanel .fgHero .fgSrc .s i{white-space:normal;text-align:left}
#forgePanel .fgHero .fgHGo{appearance:none;border:0;min-height:44px;padding:0 22px;border-radius:999px;cursor:pointer;font-weight:750;font-size:15px;margin-top:4px;
  background:linear-gradient(100deg,#ffd166,#ff9a3c);color:#1b1206;box-shadow:0 0 30px -8px rgba(255,170,60,.8)}
#forgePanel .fgHero .fgHGo.view{background:rgba(255,255,255,.1);color:#f4f3f8;box-shadow:none}
#forgePanel .fgMore{appearance:none;border:0;width:100%;margin:22px 0 0;padding:12px 14px;border-radius:14px;cursor:pointer;display:flex;align-items:center;gap:12px;
  background:rgba(255,255,255,.04);box-shadow:inset 0 0 0 1px rgba(255,255,255,.1);color:inherit;font:inherit;text-align:left}
#forgePanel .fgMore .q{display:flex}
#forgePanel .fgMore .q i{width:22px;height:30px;margin-right:-8px;border-radius:5px;display:grid;place-items:center;font-style:normal;font-weight:800;font-size:13px;color:#8a80c8;
  background:radial-gradient(circle at 50% 40%,#2a2350,#100e1d);box-shadow:inset 0 0 0 1px rgba(255,255,255,.14)}
#forgePanel .fgMore b{flex:1;font-size:16px;font-weight:650;padding-left:8px}
#forgePanel .fgMore span{font-size:15px;color:#ffd58a}
#forgePanel .fgGrid.fgLocked{margin-top:16px}
#forgePanel .fgGrid.fgLocked[hidden]{display:none}
@media (orientation:landscape) and (max-height:520px){
  #forgePanel .fgHero .dg{flex-direction:row;justify-content:center;align-items:center;gap:22px}
  #forgePanel .fgHero .dg-duo{margin:0}
  #forgePanel .fgHero .dg-slot{--dg-h:max(150px,calc(100svh - 232px))}
  #forgePanel .fgHero .dg-side{max-width:320px}
  #forgePanel .fgHero .dg-rt{width:28px;height:39px}
}
#forgePanel .fgHead{appearance:none;border:0;background:none;color:inherit;font:inherit;cursor:pointer;display:flex;align-items:baseline;
  gap:10px;width:100%;padding:12px 0 14px;text-align:left;font-family:var(--v2-ui,system-ui)}
#forgePanel .fgHead b{font-size:15px;font-weight:650;letter-spacing:.06em;text-transform:uppercase;color:#ffd58a}
#forgePanel .fgHead span{font-size:15px;color:var(--v2-text-2,#b6b4c6)}
#forgePanel .fgHead i{margin-left:auto;font-style:normal;font-size:14px;color:#ffd58a;opacity:.85}
#forgePanel .fgGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px 12px}
@media (min-width:431px){#forgePanel .fgGrid{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media (min-width:761px){#forgePanel{padding:6px 32px 48px}#forgePanel .fgGrid{grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:26px 18px}}
.fgT{appearance:none;border:0;background:none;padding:0;text-align:left;color:inherit;font:inherit;cursor:pointer;
  display:flex;flex-direction:column;gap:7px;font-family:var(--v2-ui,system-ui);min-width:0}
.fgArt{position:relative;display:block;aspect-ratio:5/7;border-radius:14px;background:#0d0d14;
  box-shadow:0 0 0 1.5px rgba(255,154,232,.55),var(--heft),0 12px 28px -16px rgba(255,154,232,.45)}
.fgArt::after{content:'';position:absolute;inset:0;border-radius:inherit;box-shadow:var(--heft-bevel);pointer-events:none;z-index:4}
.fgArt>img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:14px;display:block}
.fgT.lock .fgArt>img{opacity:.32}
.fgT.lock.duo .fgArt{box-shadow:0 0 0 1px rgba(255,255,255,.14)}
.fgQ{position:absolute;inset:0;border-radius:14px;display:grid;place-items:center;overflow:hidden;
  background:radial-gradient(80% 60% at 50% 40%,#1d1a33,#07070c 75%);box-shadow:inset 0 0 0 1px rgba(154,140,255,.28)}
.fgQ b{font-size:64px;font-weight:700;color:rgba(200,190,255,.24);text-shadow:0 0 30px rgba(154,140,255,.35)}
.fgQ small{position:absolute;bottom:12px;left:0;right:0;text-align:center;font-size:13px;font-weight:650;letter-spacing:.12em;color:#a99fff}
.fgArt .fgRing{position:absolute;inset:-3px;border-radius:17px;pointer-events:none;box-shadow:0 0 0 2px #ffd166,0 0 24px 2px rgba(255,190,80,.55);
  opacity:.55;animation:fgReady 1.8s ease-in-out infinite;z-index:4}
@keyframes fgReady{50%{opacity:1}}
@media (prefers-reduced-motion:reduce){.fgArt .fgRing{animation:none;opacity:1}}
.fgArt .ct-pw{z-index:5}
.fgArt.rdy>:not(.ct-pw):not(.fgKind):not(.fgRing):not(.fgGo):not(.fgQ){filter:grayscale(1) brightness(.6) contrast(1.05)}
.fgArt .fgGo{position:absolute;z-index:6;left:50%;bottom:14px;transform:translateX(-50%);white-space:nowrap;padding:7px 14px;
  border-radius:999px;font-size:13px;font-weight:750;letter-spacing:.05em;text-transform:uppercase;color:#1a1206;
  background:linear-gradient(180deg,#ffe08a,#ffb547);box-shadow:0 4px 18px rgba(255,180,60,.45),inset 0 1px 0 rgba(255,255,255,.6)}
.fgArt .fgKind{position:absolute;z-index:5;right:8px;top:8px;font-size:13px;font-weight:650;padding:4px 8px;border-radius:999px;
  background:rgba(8,8,14,.8);color:#ffd58a;box-shadow:inset 0 0 0 1px rgba(255,213,138,.3)}
.fgT .fgName{font-size:16px;font-weight:650;letter-spacing:-.01em;line-height:1.2;color:#f4f3f8;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fgT .fgSub{font-size:14px;color:var(--v2-text-2,#b6b4c6);font-variant-numeric:tabular-nums}
.fgSub.go{color:#ffd166;font-weight:650}
.fgSub.own{color:#9fe3c0;font-weight:600}
.fgSrc{display:flex;gap:6px}
.fgSrc .s{position:relative;flex:1 1 0;min-width:0;display:flex;align-items:center;gap:6px;padding:3px;border-radius:9px;
  background:rgba(255,255,255,.045);box-shadow:inset 0 0 0 1px rgba(255,255,255,.07)}
.fgSrc .s img{width:22px;height:30px;border-radius:4px;object-fit:cover;flex:none}
.fgSrc .s i{font-style:normal;font-size:14px;line-height:1.15;color:var(--v2-text-3,#807d94);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fgSrc .s.ok i{color:#9fe3c0}.fgSrc .s.miss img{opacity:.35}
.fgT .fgSrc .s i{display:none}.fgT .fgSrc .s{flex:0 0 auto}
.fgReq{display:flex;flex-wrap:wrap;gap:5px}
.fgReq span{font-size:14px;line-height:1;padding:6px 9px;border-radius:999px;color:#b6b4c6;
  background:rgba(255,255,255,.05);box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);font-variant-numeric:tabular-nums}
.fgReq span.ok{color:#0c0b12;background:#9fe3c0;box-shadow:none}
.fgT .fgReq span:nth-child(n+3){display:none}
#forgeExplain{position:fixed;inset:0;z-index:41;display:none;align-items:flex-end;justify-content:center;background:rgba(2,3,9,.72)}
#forgeExplain.on{display:flex}
#forgeExplain .fxIn{width:min(520px,100%);box-sizing:border-box;padding:22px 22px max(22px,env(safe-area-inset-bottom));border-radius:24px 24px 0 0;
  background:linear-gradient(170deg,#1a1630,#0b0a14 70%);box-shadow:0 -18px 50px rgba(0,0,0,.6),inset 0 1px 0 rgba(255,213,138,.25);
  font-family:var(--v2-ui,system-ui);color:#f4f3f8}
@media (min-width:761px){#forgeExplain{align-items:center}#forgeExplain .fxIn{border-radius:24px}}
#forgeExplain small{display:block;font-size:13px;font-weight:650;letter-spacing:.12em;color:#ffd58a;margin-bottom:6px}
#forgeExplain h2{margin:0 0 10px;font-size:26px;font-weight:700;letter-spacing:-.02em;line-height:1.1}
#forgeExplain p{margin:0 0 8px;font-size:17px;line-height:1.45;color:#cfccdc}
#forgeExplain p b{color:#fff}
#forgeExplain button{appearance:none;border:0;cursor:pointer;margin-top:14px;width:100%;min-height:50px;border-radius:999px;
  font:inherit;font-weight:650;font-size:16px;background:#f3f2f7;color:#0c0b12}
/* v277 THE FORGE SHEET: one glance, one gesture. The mystery card big, what it costs as
   small card faces, one huge HOLD-TO-FORGE. Fills the screen at every aspect ratio:
   portrait stacks (top / card / cost + button), landscape (>= 9:10) puts the card left. */
#forgeSheet{position:fixed;inset:0;z-index:40;display:none;overflow:hidden;font-family:var(--v2-ui,system-ui);color:#f6f4fb;--charge:0;
  background:radial-gradient(90% 55% at 50% 100%,rgba(255,120,40,.18),transparent 70%),
    radial-gradient(120% 90% at 50% 30%,#171126 0%,#08070e 60%,#030306 100%)}
#forgeSheet.on{display:block}
#forgeSheet .fsIn{position:absolute;inset:0;z-index:1;display:grid;box-sizing:border-box;gap:clamp(10px,2vmin,22px);
  grid-template-rows:auto minmax(0,1fr) auto;grid-template-areas:"top" "stage" "side";
  padding:max(14px,env(safe-area-inset-top)) max(16px,env(safe-area-inset-right)) max(16px,env(safe-area-inset-bottom)) max(16px,env(safe-area-inset-left))}
@media (min-aspect-ratio:9/10){
  #forgeSheet .fsIn{grid-template-columns:minmax(0,1.15fr) minmax(300px,1fr);grid-template-rows:minmax(0,1fr) auto auto minmax(0,1fr);
    grid-template-areas:"stage ." "stage top" "stage side" "stage .";row-gap:clamp(18px,4vmin,44px);column-gap:clamp(20px,4vw,96px);padding-left:max(24px,4vw);padding-right:max(24px,4vw);margin:0 auto;width:min(100%,200vh)}
  #forgeSheet .fsTtl{text-align:center}
}
#forgeSheet .fsTop{grid-area:top;display:flex;align-items:flex-start;gap:12px;min-width:0}
#forgeSheet .fsTtl{flex:1;min-width:0}
#forgeSheet .fsTtl small{display:block;font-size:clamp(13px,2.1vmin,28px);font-weight:750;letter-spacing:.28em;color:#ffd08a;margin-bottom:.35em}
#forgeSheet .fsTtl h2{margin:0;font-size:clamp(30px,6vmin,88px);font-weight:800;letter-spacing:-.025em;line-height:1.02;overflow-wrap:anywhere}
#forgeSheet .fsX{appearance:none;flex:none;width:clamp(44px,6.5vmin,60px);height:clamp(44px,6.5vmin,60px);border-radius:50%;border:0;cursor:pointer;
  background:rgba(255,255,255,.09);color:#f6f4fb;font-size:clamp(18px,2.6vmin,24px)}
@media (min-aspect-ratio:9/10){#forgeSheet .fsX{position:absolute;top:max(14px,env(safe-area-inset-top));right:max(16px,env(safe-area-inset-right))}
}
#forgeSheet .fsStage{grid-area:stage;min-height:0;min-width:0;container-type:size;display:grid;place-items:center}
#forgeSheet .fsCard{position:relative;display:block;height:min(100cqh,100cqw * 1.397);aspect-ratio:1.26/1.76;border-radius:clamp(14px,2.2vmin,24px);
  background:#0d0d14;--ch:min(100cqh,100cqw * 1.397);
  box-shadow:0 30px 90px -30px #000,0 0 40px -10px rgba(255,150,60,.35)}
#forgeSheet .fsCard::before{content:'';position:absolute;inset:-14%;z-index:-1;border-radius:30%;pointer-events:none;will-change:opacity;
  background:radial-gradient(closest-side,rgba(255,170,70,.75),rgba(255,110,30,.35) 55%,transparent);opacity:calc(var(--charge) * 1);transition:opacity .16s ease-out}
#forgeSheet .fsCard>img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit}
#forgeSheet .fsQ{position:absolute;inset:0;border-radius:inherit;overflow:hidden;display:grid;place-items:center;
  background:radial-gradient(70% 50% at 50% 42%,var(--qc,#3a2c6e) 0%,#120f22 60%,#06060b 100%);box-shadow:inset 0 0 0 1.5px rgba(200,185,255,.3)}
#forgeSheet .fsQ::before{content:'';position:absolute;left:50%;top:42%;width:150%;aspect-ratio:1;translate:-50% -50%;
  background:conic-gradient(from 0deg,transparent 0 20%,rgba(255,220,160,.22) 25%,transparent 32% 70%,rgba(180,160,255,.2) 76%,transparent 82%);
  animation:fsSpin 9s linear infinite}
#forgeSheet .fsQ b{position:relative;font-size:calc(var(--ch) * .36);font-weight:800;line-height:1;color:rgba(235,228,255,.5);
  text-shadow:0 0 calc(var(--ch) * .08) rgba(170,150,255,.7)}
#forgeSheet .fsPw{position:absolute;left:0;right:0;bottom:0;z-index:7;display:flex;align-items:baseline;justify-content:center;gap:.35em;
  padding:calc(var(--ch) * .03) 0 calc(var(--ch) * .035);border-radius:0 0 clamp(14px,2.2vmin,24px) clamp(14px,2.2vmin,24px);font-size:calc(var(--ch) * .052);font-weight:700;color:#e9e5f7;
  background:linear-gradient(0deg,rgba(4,4,10,.85),rgba(4,4,10,0));font-variant-numeric:tabular-nums}
#forgeSheet .fsPw b{font-size:1.9em;font-weight:800;color:#ffd08a;letter-spacing:-.02em}
#forgeSheet .fsPw i{font-style:normal;opacity:.75}
#forgeSheet .fsHeat{position:absolute;inset:0;z-index:8;border-radius:inherit;pointer-events:none;opacity:calc(var(--charge) * .85);
  background:radial-gradient(80% 60% at 50% 100%,rgba(255,230,180,.9),rgba(255,120,30,.55) 45%,transparent 75%);transition:opacity .16s ease-out}
#forgeSheet.charging .fsCard{animation:fsShake .08s linear infinite}
@keyframes fsShake{0%{transform:translate(calc(var(--charge) * -3px),0) scale(calc(1 + .03 * var(--charge)))}
  50%{transform:translate(calc(var(--charge) * 3px),calc(var(--charge) * -2px)) scale(calc(1 + .03 * var(--charge)))}
  100%{transform:translate(calc(var(--charge) * -3px),0) scale(calc(1 + .03 * var(--charge)))}}
@keyframes fsSpin{to{rotate:360deg}}
#forgeSheet .fsSide{grid-area:side;display:flex;flex-direction:column;align-items:stretch;gap:clamp(12px,2.4vmin,26px);min-width:0;
  width:100%;max-width:min(860px,100%);justify-self:center}
#forgeSheet .fsCostH{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:center;gap:.2em .6em;font-size:clamp(17px,2.7vmin,38px);color:#cfcadd}
#forgeSheet .fsCostH b{color:#fff;font-weight:750}
#forgeSheet .fsFaces{display:flex;justify-content:center;align-items:flex-end;margin-top:clamp(10px,1.8vmin,18px);min-height:20px;--fw:64px;--ov:0px}
#forgeSheet .fsFace{position:relative;flex:none;width:var(--fw);aspect-ratio:1.26/1.76;border-radius:calc(var(--fw) * .1);overflow:hidden;background:#15131f;
  box-shadow:0 0 0 1.5px rgba(255,210,150,.4),0 8px 20px -8px #000,0 10px 26px -12px rgba(255,130,40,.9)}
#forgeSheet .fsFace+.fsFace{margin-left:calc(clamp(6px,1.2vmin,12px) - var(--ov))}
#forgeSheet .fsFace img{width:100%;height:100%;object-fit:cover;display:block}
#forgeSheet .fsMore{display:grid;place-items:center;font:800 clamp(14px,2.6vmin,22px) Unbounded,var(--v2-ui,sans-serif);color:#ffe2a8}
#forgeSheet .fsFace::after{content:'';position:absolute;inset:0;background:linear-gradient(0deg,rgba(255,120,30,.45),transparent 55%)}
#forgeSheet .fsFace.miss img{opacity:.25;filter:grayscale(1)}
#forgeSheet .fsFace.miss::after{background:none}
#forgeSheet .fsReq{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;margin-top:12px}
#forgeSheet .fsReq span{font-size:clamp(15px,2.3vmin,22px);padding:.4em .75em;border-radius:999px;color:#d6d2e4;background:rgba(255,255,255,.07);
  box-shadow:inset 0 0 0 1px rgba(255,255,255,.1);font-variant-numeric:tabular-nums}
#forgeSheet .fsReq span.ok{color:#0c0b12;background:#9fe3c0;box-shadow:none}
#forgeSheet .fsAdvL{appearance:none;border:0;background:none;cursor:pointer;font:inherit;font-size:clamp(13px,1.8vmin,22px);color:rgba(255,255,255,.42);
  text-decoration:underline;text-underline-offset:3px;padding:6px 10px;margin:4px auto 0;display:block}
#forgeSheet .fsGo{appearance:none;border:0;cursor:pointer;position:relative;overflow:hidden;width:100%;touch-action:none;user-select:none;-webkit-user-select:none;
  -webkit-touch-callout:none;min-height:clamp(70px,11.5vmin,160px);border-radius:999px;font:inherit;color:#1b0f04;--p:0;
  background:linear-gradient(100deg,#ffe08a,#ffad42 55%,#ff7a2a);
  box-shadow:0 0 30px -6px rgba(255,160,50,.85),inset 0 2px 0 rgba(255,255,255,.55),inset 0 -4px 0 rgba(120,40,0,.3);
  transition:transform .12s}
#forgeSheet .fsGo:active{transform:scale(.985)}
#forgeSheet .fsGo .fsGoFill{position:absolute;inset:0;transform-origin:0 50%;transform:scaleX(var(--p));
  background:linear-gradient(90deg,rgba(255,255,255,.35),rgba(255,255,240,.95));mix-blend-mode:screen}
#forgeSheet .fsGo .fsGoT{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1}
#forgeSheet .fsGo .fsGoT b{font-size:clamp(28px,5.4vmin,76px);font-weight:850;letter-spacing:.14em;text-transform:uppercase}
#forgeSheet .fsGo .fsGoT small{font-size:clamp(12px,1.9vmin,24px);font-weight:700;letter-spacing:.24em;text-transform:uppercase;opacity:.7;margin-top:.35em}
#forgeSheet .fsGo.nudge .fsGoT small{opacity:1;animation:fsNudge .5s ease-in-out 2}
@keyframes fsNudge{50%{transform:scale(1.18)}}
#forgeSheet .fsGo:disabled{cursor:default;background:rgba(255,255,255,.1);color:rgba(255,255,255,.55);box-shadow:none}
#forgeSheet .fsGo:focus-visible{outline:3px solid #fff;outline-offset:4px}
@media (prefers-reduced-motion:reduce){#forgeSheet .fsQ::before,#forgeSheet.charging .fsCard,#forgeSheet .fsGo.nudge .fsGoT small{animation:none}}
#forgeSheet .fsAdv{position:absolute;left:0;right:0;bottom:0;z-index:3;max-height:78%;overflow-y:auto;box-sizing:border-box;
  padding:18px max(16px,env(safe-area-inset-right)) max(18px,env(safe-area-inset-bottom)) max(16px,env(safe-area-inset-left));
  border-radius:22px 22px 0 0;background:#110f1b;box-shadow:0 -20px 60px rgba(0,0,0,.7),inset 0 1px 0 rgba(255,255,255,.1)}
#forgeSheet .fsAdv .fsAdvH{display:flex;align-items:center;gap:10px;margin-bottom:10px}
#forgeSheet .fsAdv .fsAdvH b{flex:1;font-size:17px}
#forgeSheet .fsAdv .fsAdvH button{appearance:none;border:0;cursor:pointer;min-height:44px;padding:0 20px;border-radius:999px;background:#f3f2f7;color:#0c0b12;font:inherit;font-weight:650}
#forgeSheet .fsAdv p{margin:0 0 10px;font-size:14px;color:#b6b4c6}
#forgeSheet .fsRea{display:grid;grid-template-columns:repeat(auto-fill,minmax(72px,1fr));gap:8px}
#forgeSheet .fsRea button{appearance:none;border:0;padding:0;background:none;cursor:pointer;color:inherit;font:inherit;
  display:flex;flex-direction:column;gap:3px;min-width:0;text-align:left}
#forgeSheet .fsRea img{width:100%;aspect-ratio:1.26/1.76;object-fit:cover;border-radius:7px;display:block;box-shadow:inset 0 0 0 1px rgba(255,255,255,.1)}
#forgeSheet .fsRea button.sel img{box-shadow:0 0 0 2px #ffd166}
#forgeSheet .fsRea i{font-style:normal;font-size:11px;color:#b6b4c6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#forgeSheet .fsSwap{margin-top:12px;padding:12px;border-radius:14px;background:rgba(255,255,255,.04);box-shadow:inset 0 0 0 1px rgba(255,255,255,.08)}
`;

let C = null;            // { deck, byId, V, artSrc, typeGlyph, pwr, identity, audio, haptics, toast, onForged }
let tab = 'cards';
let busy = false;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

export function initForgeUI(ctx) {
  C = ctx;
  ensureForgedFrameCss();
  if (!document.getElementById('forgeUiCss')) {
    const st = document.createElement('style'); st.id = 'forgeUiCss'; st.textContent = CSS;
    document.head.appendChild(st);
  }
  const binder = document.getElementById('binder');
  if (!binder || document.getElementById('forgePanel')) return;
  // COLLECTION 2: the Cards/Duos/Fusions tabs became one type strip
  // (#binderTabs in index.html, painted by main.js); this module owns the
  // Duos/Fusions panel, their explainer and the forge sheet.
  const panel = document.createElement('div');
  panel.id = 'forgePanel';
  document.getElementById('binderGrid').after(panel);
  panel.addEventListener('click', (e) => {
    if (e.target.closest('.fgHead')) { explain(tab); return; }
    const tp = e.target.closest('.tpT.own');                 // v257 the Trophies shelf (src/trophies.js)
    if (tp) { if (C.onTrophy) C.onTrophy(tp.dataset.trophy); return; }
    const mo = e.target.closest('.fgMore');
    if (mo) {
      const g = mo.nextElementSibling, open = g.hidden;
      g.hidden = !open; mo.setAttribute('aria-expanded', String(open)); mo.querySelector('span:last-child').textContent = open ? 'Hide' : 'Show';
      if (C.audio) C.audio.ui('tick');
      if (open) { wireForgedVideos(g); idleOffscreenFrames(g); }
      return;
    }
    const hg = e.target.closest('.fgHGo');
    if (hg) { if (C.onFocus) C.onFocus(hg.dataset.id); else openSheet(hg.dataset.id); return; }
    const t = e.target.closest('.fgT');
    if (t) (C.onFocus ? C.onFocus(t.dataset.id) : openSheet(t.dataset.id));
  });
  const ex = document.createElement('div');
  ex.id = 'forgeExplain'; ex.setAttribute('role', 'dialog'); ex.setAttribute('aria-modal', 'true');
  ex.addEventListener('click', (e) => { if (e.target === ex || e.target.closest('button')) closeExplain(); });
  document.body.appendChild(ex);
  const sheet = document.createElement('div');
  sheet.id = 'forgeSheet'; sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true');
  document.body.appendChild(sheet);
  // Back during the forge moment: never the screen underneath — it jumps to the final blow, or leaves the reveal
  nav.registerLevel({ name: 'forgeCer', z: 71, isOpen: () => !!document.getElementById('forgeCer'),
    close: () => { const c = window.__nmForgeCer; if (!c || c.done) return; if (c.phase === 'ready') c.skip && c.skip(); else if (c.rush) c.rush(); } });
  paintTabs();
}

export function setTab(next) {
  tab = next;
  const binder = document.getElementById('binder');
  binder.classList.toggle('forge-on', tab !== 'cards');
  const ttl = binder.querySelector('.btitle');
  if (ttl) ttl.textContent = tab === 'cards' ? 'Collection' : 'Forge';
  if (tab !== 'duos') dropHero();
  if (C.audio) C.audio.play('tick', { gain: 0.5 });
  if (tab !== 'cards') { renderPanel(); document.getElementById('forgePanel').scrollTop = 0; markForgeSeen(); }
  paintTabs();
  if (C.onTab) C.onTab(tab);
  // first visit to each section explains it once; its header/tab explains it again any time
  if (tab !== 'cards') {
    const key = `nomadmon-explain-${tab}`;
    let seen = true;
    try { seen = localStorage.getItem(key) === '1'; localStorage.setItem(key, '1'); } catch { /* private */ }
    if (!seen) explain(tab);
  }
}

// ---------------------------------------------------------------- explainer
const EXPLAIN = {
  duos: ['Duos', 'Two cards that belong together, forged into <b>one of the most powerful cards you can own</b>.',
    'Own both cards, then Forge. Both are spent.'],
  fusions: ['Fusions', 'The rarest power in the game: <b>many cards forged into one hidden legend</b>.',
    'The recipe is shown. Who you get stays secret until you forge.'],
};
export function explain(which) {
  const e = EXPLAIN[which];
  const el = document.getElementById('forgeExplain');
  if (!e || !el) return;
  el.innerHTML = `<div class="fxIn"><small>${which === 'duos' ? 'TWO CARDS · ONE LEGEND' : 'MANY CARDS · ONE LEGEND'}</small>`
    + `<h2>${e[0]}</h2><p>${e[1]}</p><p>${e[2]}</p><button type="button">Got it</button></div>`;
  el.classList.add('on');
  setTimeout(() => el.querySelector('button')?.focus({ preventScroll: true }), 30);
}
export function closeExplain() { document.getElementById('forgeExplain')?.classList.remove('on'); }
export function explainOpen() { return !!document.getElementById('forgeExplain')?.classList.contains('on'); }
export function currentTab() { return tab; }

// ---------------------------------------------------------------- data views
const owned = (id) => C.identity.ownedCount(id) > 0;
const count = (id) => C.identity.cardCount ? C.identity.cardCount(id) : C.identity.ownedCount(id);
const lv = (id) => C.identity.cardLevel(id);
const xpOf = (id) => C.identity.cardProgress(id).xp;
// econfix: copies a pending trade holds (and, while one does, copies cooling from a trade) stay put (forged.js copyAvailability)
const reservedOf = (id) => (C.identity.cardReserved ? C.identity.cardReserved(id) : 0);
const coolingOf = (id) => (C.identity.cardCooling ? C.identity.cardCooling(id) : 0);
const spendable = (id) => spendableCopies(count(id), reservedOf(id), coolingOf(id));
const spare = (id) => count(id) > 1 && spendable(id) >= 1;      // spending one copy keeps the card
const trained = (id) => lv(id) >= 2;                            // a last copy carrying XP
function eligibleOwned() {
  const out = [];
  for (const d of C.deck) if (reagentEligible(d) && spendable(d.id) > 0) out.push(d);
  return out.sort((a, b) => valueOf(a) - valueOf(b) || a.id.localeCompare(b.id));
}
function valueOf(d) { return reagentValue(d, lv(d.id), spare(d.id) ? 2 : 1, C.pwr, xpOf(d.id)); }
// the squad a forge must leave: distinct owned fighters (the squad screen's own pool rule) and the
// five worth keeping -- the saved squad first, then the strongest by levelled power
function squadCtx() {
  // (INK DUEL champions live outside the collection: the server's count never includes them)
  const fighters = C.deck.filter(d => d.role !== 'support' && d.forged !== 'champion' && C.identity.ownedCount(d.id) > 0);
  let saved = [];
  try { saved = (JSON.parse(localStorage.getItem('nomadmon-deck-v1')) || {}).chars || []; } catch { /* fresh */ }
  const ok = new Set(fighters.map(d => d.id));
  const keep = new Set(saved.filter(id => ok.has(id)).slice(0, MIN_SQUAD_FIGHTERS));
  for (const d of fighters.slice().sort((a, b) => effectivePwr(b, lv(b.id)) - effectivePwr(a, lv(a.id)) || a.id.localeCompare(b.id))) {
    if (keep.size >= MIN_SQUAD_FIGHTERS) break;
    keep.add(d.id);
  }
  return { fighters: fighters.length, keep, spare, trained };
}
const shortAfter = (ids) => { const o = squadCtx(), left = fightersAfter(ids, o); return { left, short: squadTooSmall(left, o.fighters) }; };
export function duoState(r) {
  const have = r.sources.map(id => spendable(id) > 0);
  const all = !owned(r.id) && have.every(Boolean);
  const sq = all ? shortAfter(r.sources) : null;
  const left = sq ? sq.left : null;
  const blocked = !!sq && sq.short;
  return { owned: owned(r.id), have, ready: all && !blocked, blocked, left };
}
export function fusionState(r, pool = eligibleOwned()) {
  if (owned(r.id)) return { owned: true, ready: false, pick: null, terms: reqTerms(r.req, []) };
  const allowed = r.req.rarities && r.req.rarities.length ? new Set(r.req.rarities) : null;
  const terms = reqTerms(r.req, allowed ? pool.filter(c => allowed.has(c.rarity)) : pool);
  const plan = planFusion(r.req, pool, squadCtx());
  return { owned: false, ready: !!plan && !plan.blocked, blocked: !!plan && plan.blocked, left: plan ? plan.left : null, pick: plan ? plan.pick : null, terms };
}
// the title's Forge item lights only for news: a ready Duo, or a Fusion that
// SPARE copies alone can make (never a last copy), the player has not yet
// seen. Seen ids live in localStorage; opening the forge marks them seen.
const SEEN_KEY = 'nomadmon-forge-seen-v1';
function seenSet() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY)) || []); } catch { return new Set(); }
}
export function freshReady() {
  if (!C) return [];
  const seen = seenSet();
  const out = duos().filter(r => !seen.has(r.id) && duoState(r).ready).map(r => r.id);
  const spares = eligibleOwned().filter(d => spare(d.id));
  for (const r of fusions()) if (!seen.has(r.id) && !owned(r.id) && autoSelect(r.req, spares)) out.push(r.id);
  return out;
}
export function markForgeSeen() {
  const fresh = freshReady();
  if (!fresh.length) return;
  const seen = seenSet();
  for (const id of fresh) seen.add(id);
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...seen])); } catch { /* private */ }
}
// spare copies contribute a fresh copy (XP 0); a last copy brings its own XP
function startLevel(ids) {
  return levelFromXp(mintXp(ids.map(id => (spare(id) ? 0 : xpOf(id))), MAX_XP));
}
// XP the forge destroys: what the spent last copies carry, minus what the mint keeps
function xpLost(ids) {
  const spent = ids.reduce((n, id) => n + (spare(id) ? 0 : xpOf(id)), 0);
  return Math.max(0, spent - mintXp(ids.map(id => (spare(id) ? 0 : xpOf(id))), MAX_XP));
}
const SHORT = (left) => `<div class="fsCostH"><b>Leaves ${left} fighter${left === 1 ? '' : 's'}</b><span>You need ${MIN_SQUAD_FIGHTERS} to battle</span></div>`;

export function paintTabs() {
  if (!C) return;
  const ds = duos(), fs = fusions();
  const pool = eligibleOwned();
  const dOwn = ds.filter(r => owned(r.id)).length, fOwn = fs.filter(r => owned(r.id)).length;
  const dReady = ds.some(r => duoState(r).ready);
  const fReady = fs.some(r => !owned(r.id) && fusionState(r, pool).ready);
  const bd = document.querySelector('#binderTabs [data-tab="duos"]');
  const bf = document.querySelector('#binderTabs [data-tab="fusions"]');
  if (!bd) return;
  bd.querySelector('.ct').textContent = `${dOwn}/${ds.length}`;
  bf.querySelector('.ct').textContent = `${fOwn}/${fs.length}`;
  bd.classList.toggle('ready', dReady);
  bf.classList.toggle('ready', fReady);
}

// ---------------------------------------------------------------- tiles
function img(d, size = 'thumb') { return esc(C.artSrc(d, C.V, size)); }
const stateOf = (r) => (r.kind === 'duo' ? duoState(r) : fusionState(r));
// locked = shown as a "?": a Fusion until owned (never spoil it), a Duo until ready.
// Ready-but-unforged: a Duo goes muted b&w; both get the Ready-to-forge banner.
const lockedOf = (r, s) => !s.owned && (r.kind === 'fusion' || !s.ready);
// The card face of a Duo/Fusion: art + its forged frame (forged-frame.js) or
// the locked "?" — ONE render path shared by the grid tile, the carousel and
// the focus view, so the Duo/Fusion visual upgrades plug in everywhere.
// POWER is always on the face; LEVEL once owned (★ squad cost before).
export function faceHtml(id, size = 'thumb', st = null) {
  const r = recipe(id);
  const d = C.byId[id];
  if (!r || !d) return '';
  const s = st || stateOf(r);
  const hidden = lockedOf(r, s);
  // v12b: forged-card.js owns the look — the living video (a Duo's is its
  // liquid A<->B cycle) + the frame; a locked Duo/Fusion stays a "?"
  const art = hidden ? `<span class="fgQ"><b>?</b><small>${r.kind === 'duo' ? 'DUO' : `TIER ${r.tier}`}</small></span>`
    : `<img ${size === 'full' ? '' : 'loading="lazy" decoding="async" '}src="${img(d, size)}" alt="">${forgedLayersHtml(d, { size, autoplay: size === 'full', frame: r.kind === 'duo' || s.owned, srcs: r.kind === 'duo' ? r.sources.map(x => C.byId[x]) : null })}`;
  const pw = `<span class="ct-pw"><b>${C.pwr(d)}</b><span><i>PWR</i><i>${s.owned ? `LV ${lv(r.id)}` : `★${r.stars}`}</i></span></span>`;
  const rdy = s.ready && !s.owned;
  return `<span class="fgArt${rdy ? ' rdy' : ''}"${r.kind === 'duo' && !hidden ? ` data-duo="${r.id}"` : ''}>${art}${pw}${hidden ? '' : r.kind === 'duo' ? `<span class="fgKind">Duo${s.owned ? ` ${TILT_SVG}` : ''}</span>` : `<span class="fgKind">T${r.tier}</span>`}${s.ready ? '<span class="fgRing"></span>' : ''}${rdy ? '<span class="fgGo">Ready to forge</span>' : ''}</span>`;
}
function sourcesHtml(r, s, full = false) {
  return `<span class="fgSrc">${r.sources.map((sid, i) => {
    const c = C.byId[sid];
    return `<span class="s ${s.have[i] ? 'ok' : 'miss'}"><img src="${img(c)}" alt=""><i>${s.have[i] ? '✓ ' : ''}${full && s.have[i] ? `LV ${lv(sid)} · ` : ''}${esc(c ? c.name : sid)}</i></span>`;
  }).join('')}</span>`;
}
function termsHtml(s) {
  return `<span class="fgReq">${s.terms.map(t => `<span class="${t.ok ? 'ok' : ''}">${esc(t.label)}${t.ok ? ' ✓' : ` ${t.have}/${t.need}`}</span>`).join('')}</span>`;
}
function stateLine(r, s) {
  if (s.owned) return `<span class="fgSub own">Unlocked · LV ${lv(r.id)}</span>`;
  if (s.ready) return '<span class="fgSub go">Ready to forge</span>';
  if (s.blocked) return `<span class="fgSub">Leaves under ${MIN_SQUAD_FIGHTERS} fighters</span>`;
  return r.kind === 'duo' ? `<span class="fgSub">${s.have.filter(Boolean).length}/2 cards owned</span>`
    : `<span class="fgSub">${esc(r.reqText)}</span>`;
}
function duoTile(r) {
  const d = C.byId[r.id];
  const s = duoState(r);
  const cls = s.owned ? 'own' : s.ready ? 'ready' : 'lock';
  const name = lockedOf(r, s) ? 'Unknown Duo' : d.name;
  return `<button class="fgT duo ${cls}" type="button" data-id="${r.id}" aria-label="${esc(name)}, Duo, power ${C.pwr(d)}">
    ${faceHtml(r.id, 'thumb', s)}<span class="fgName">${esc(name)}</span>${stateLine(r, s)}${sourcesHtml(r, s)}</button>`;
}
function fusionTile(r, pool) {
  const d = C.byId[r.id];
  const s = fusionState(r, pool);
  const cls = s.owned ? 'own' : s.ready ? 'ready' : 'lock';
  return `<button class="fgT fusion ${cls}" type="button" data-id="${r.id}" aria-label="${esc(s.owned ? d.name : `Fusion tier ${r.tier}`)}, power ${C.pwr(d)}">
    ${faceHtml(r.id, 'thumb', s)}<span class="fgName">${esc(s.owned ? d.name : 'Unknown Fusion')}</span>${stateLine(r, s)}${s.owned ? '' : termsHtml(s)}</button>`;
}
export function renderPanel() {
  const panel = document.getElementById('forgePanel');
  if (!panel || tab === 'cards') return;
  if (tab === 'trophies') { if (C.renderTrophies) C.renderTrophies(panel); gridLens(panel); return; }   // v257 (main.js + src/trophies.js)
  const rs = tab === 'duos' ? duos() : fusions();
  const own = rs.filter(r => owned(r.id)).length;
  const head = `<button class="fgHead" type="button" aria-label="What are ${tab === 'duos' ? 'Duos' : 'Fusions'}?"><b>${tab === 'duos' ? 'Duos' : 'Fusions'}</b>`
    + `<span>${own} of ${rs.length} unlocked</span><i>What are ${tab === 'duos' ? 'Duos' : 'Fusions'}?</i></button>`;
  if (tab === 'duos') {
    // FORGE leads with spectacle: a giant lenticular Duo (yours first, then the showcase), then the Duos you
    // own / can forge / are one card from; the rest collapse into one "N more to discover" row
    const st = new Map(rs.map(r => [r.id, duoState(r)]));
    const near = (r) => { const s = st.get(r.id); return s.owned || s.ready || s.have.some(Boolean); };
    const rank = (r) => { const s = st.get(r.id); return s.ready ? 0 : s.owned ? 1 : 2; };
    const mine = rs.filter(near).sort((a, b) => rank(a) - rank(b)), rest = rs.filter(r => !near(r));
    const more = rest.length ? `<button class="fgMore" type="button" aria-expanded="false"><span class="q">${rest.slice(0, 4).map(() => '<i>?</i>').join('')}</span>`
      + `<b>${rest.length} more Duo${rest.length === 1 ? '' : 's'} to discover</b><span>Show</span></button>`
      + `<div class="fgGrid fgLocked" hidden>${rest.map(duoTile).join('')}</div>` : '';
    dropHero();
    panel.innerHTML = head + '<div class="fgHero"></div>' + (mine.length ? `<div class="fgGrid">${mine.map(duoTile).join('')}</div>` : '') + more;
    mountHero(panel, rs, st);
  } else {
    const pool = eligibleOwned();
    panel.innerHTML = head + `<div class="fgGrid">${rs.map(r => fusionTile(r, pool)).join('')}</div>`;
  }
  wireForgedVideos(panel);
  idleOffscreenFrames(panel);
  gridLens(panel);
}
// GRID LENS (Duos tab): with device tilt live, the most central visible owned
// Duo follows it; without, ONE random visible owned Duo shimmers (a quick
// sweep of two stills) every 6-8 s while the grid sits idle. Never while
// scrolling (a scroll stops the lens; nothing restarts for 700 ms), never
// under focus / a sheet, never more than one lens. IntersectionObserver keeps
// the visible set, so the 500 ms tick never measures the whole grid.
// ---------------------------------------------------------------- the hero (duo-gallery.js)
let HERO = null;
function dropHero() { if (HERO) { const h = HERO; HERO = null; if (h.io) h.io.disconnect(); if (h.gal) h.gal.destroy(); } }
const heroShown = () => !!HERO && HERO.vis && tab === 'duos' && HERO.panel.isConnected && HERO.panel.offsetParent !== null && !document.hidden
  && !document.body.classList.contains('cfocus') && !(sheetEl() && sheetEl().classList.contains('on')) && !document.getElementById('forgeCer');
function mountHero(panel, rs, st) {
  const host = panel.querySelector('.fgHero');
  if (!host) return;
  // your Duos first (owned, then ready), then the curated showcase (they may always be revealed)
  const yours = rs.filter(r => st.get(r.id).owned || st.get(r.id).ready).map(r => r.id);
  const ids = [...new Set([...yours, ...SHOWCASE_DUOS])].filter(id => recipe(id) && C.byId[id]).slice(0, 8);
  const h = HERO = { panel, vis: true, io: null, gal: null };
  h.gal = mountDuoGallery(host, {
    ids, card: (id) => C.byId[id], srcsOf: (id) => { const r = recipe(id); return r ? r.sources.map(x => C.byId[x]) : null; },
    artSrc: (d, size) => C.artSrc(d, C.V, size),
    lens: { tilt: duoLensTilt, sweep: duoLensSweep, follow: duoLensFollow, release: duoLensRelease, layers: forgedLayersHtml, wire: wireForgedVideos, TILT_SVG, DRAG_SVG },
    tilt: C.tilt || null, reduced: matchMedia('(prefers-reduced-motion: reduce)').matches, when: () => HERO === h && heroShown(),
    sound: (n) => C.audio && C.audio.play(n, { gain: 0.3, rate: 1.4 }),
    info: (d) => {
      const r = recipe(d.id), s = duoState(r);
      const act = s.ready ? `<button class="fgHGo" type="button" data-id="${d.id}">Forge it</button>`
        : s.owned ? `<button class="fgHGo view" type="button" data-id="${d.id}">View</button>` : '';
      return `<b>${esc(d.name)}</b>${stateLine(r, s)}${sourcesHtml(r, s)}${act}`;
    },
  });
  if (typeof IntersectionObserver === 'function') {
    h.io = new IntersectionObserver((es) => { for (const e of es) h.vis = e.isIntersecting && e.intersectionRatio > 0.35; }, { root: panel, threshold: [0, 0.35, 0.7] });
    h.io.observe(host);
  }
}

let G = null;
const gridShown = (panel) => tab === 'duos' && !heroShown() && panel.isConnected && panel.offsetParent !== null && !document.hidden
  && !document.body.classList.contains('cfocus') && !(sheetEl() && sheetEl().classList.contains('on')) && !document.getElementById('forgeCer');
function gridLens(panel) {
  if (G) { G.io.disconnect(); clearInterval(G.iv); G.panel.removeEventListener('scroll', G.onScroll); if (G.sw) G.sw.stop(true); G = null; }
  if (tab !== 'duos' || typeof IntersectionObserver !== 'function') return;
  const tiles = panel.querySelectorAll('.fgT.duo.own .fgArt[data-duo]');
  if (!tiles.length) return;
  const g = G = { panel, vis: new Set(), scrollAt: 0, next: performance.now() + 2500, sw: null };
  g.io = new IntersectionObserver((es) => { for (const e of es) { if (e.isIntersecting && e.intersectionRatio > 0.6) g.vis.add(e.target); else g.vis.delete(e.target); } },
    { root: panel, threshold: [0, 0.6, 1] });
  for (const t of tiles) g.io.observe(t);
  g.onScroll = () => { g.scrollAt = performance.now(); if (g.sw) { g.sw.stop(true); g.sw = null; } };
  panel.addEventListener('scroll', g.onScroll, { passive: true });
  g.iv = setInterval(() => gridTick(g), 500);
}
function gridTick(g) {
  if (G !== g) return;
  const now = performance.now();
  if (!gridShown(g.panel) || now - g.scrollAt < 700) return;
  const vis = [...g.vis].filter(a => a.isConnected);
  if (!vis.length) return;
  if (lensGyroLive()) {            // the tilt itself is the show: the most central Duo follows it
    const pr = g.panel.getBoundingClientRect(), cx = (pr.left + pr.right) / 2, cy = (pr.top + pr.bottom) / 2;
    let best = null, bd = Infinity;
    for (const a of vis) { const r = a.getBoundingClientRect(); const d = Math.hypot((r.left + r.right) / 2 - cx, (r.top + r.bottom) / 2 - cy); if (d < bd) { bd = d; best = a; } }
    duoLensFollow(best, duoSrcs(best.dataset.duo), { still: true, size: 'thumb', when: () => G === g && gridShown(g.panel) && performance.now() - g.scrollAt > 250 });
    return;
  }
  if (now < g.next || duoLensActive() || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  g.next = now + 6000 + Math.random() * 2000;
  const a = vis[Math.floor(Math.random() * vis.length)];
  g.sw = duoLensSweep(a, duoSrcs(a.dataset.duo), { dur: 1100, still: true, size: 'thumb' });
}
/** The two source card defs of a Duo id (null for anything else). */
export function duoSrcs(id) { const r = recipe(id); return r && r.kind === 'duo' ? r.sources.map(x => C.byId[x]) : null; }
/** Does the player own this forged card? */
export const ownsForged = (id) => owned(id);
/** An automatic lens sweep on a Duo art box (`.fgArt[data-duo]`). */
export function duoSweep(art, opts) { return art ? duoLensSweep(art, duoSrcs(art.dataset.duo), opts) : null; }
/** Start the forged videos inside a freshly rendered face (carousel/focus). */
export function wireFaces(root) { wireForgedVideos(root); }
/** Lenticular: the focus view's lens position p (-1 face A .. 1 face B) drives a Duo face. */
export function duoTilt(root, p, driving, opts) {
  const art = root && root.querySelector('.fgArt[data-duo]');
  if (!art) return;
  const r = recipe(art.dataset.duo);
  if (r) duoLensTilt(art, r.sources.map(id => C.byId[id]), p, driving, opts);
}
// ---- the shared Collection carousel + focus view ask for these
export function list(which) { return which === 'trophies' ? [] : (which === 'duos' ? duos() : fusions()).map(r => r.id); }
export function isHidden(id) { const r = recipe(id); return !!r && lockedOf(r, stateOf(r)); }
const lockedName = (r, d, s) => (lockedOf(r, s) ? `Unknown ${r.kind === 'duo' ? 'Duo' : 'Fusion'}` : d.name);
export function captionHtml(id) {
  const r = recipe(id); const d = C.byId[id];
  if (!r || !d) return '';
  const s = stateOf(r);
  return `<div class="bc-name">${esc(lockedName(r, d, s))}</div>`
    + `<div class="bc-sub"><span style="color:#ffd58a">${r.kind === 'duo' ? 'Duo' : `Fusion · Tier ${r.tier}`}</span>${stateLine(r, s)}</div>`
    + `<div class="bc-stat"><b>${C.pwr(d)}</b><span>PWR</span>${s.owned ? `<b>${lv(id)}</b><span>LV</span>` : `<b>★${r.stars}</b><span>cost</span>`}</div>`;
}
export function focusInfoHtml(id) {
  const r = recipe(id); const d = C.byId[id];
  if (!r || !d) return '';
  const s = stateOf(r);
  const hidden = lockedOf(r, s);
  const what = s.owned ? '' : s.blocked ? `Forging now would leave under ${MIN_SQUAD_FIGHTERS} fighters to battle.` : s.ready
    ? (r.kind === 'duo' ? 'Forging spends both cards.' : 'Forge it to find out who it is.')
    : r.kind === 'duo' ? 'Own both cards to forge this Duo, one of the most powerful cards you can get.'
      : 'Forge it from the cards below. Who it is stays secret until you do.';
  const forge = s.ready ? `<button class="cf-forge" type="button" data-id="${r.id}">Forge ${r.kind === 'duo' ? 'Duo' : 'Fusion'}</button>` : '';
  return `<div class="cf-sub"><span style="color:#ffd58a">${r.kind === 'duo' ? 'Duo' : `Fusion · Tier ${r.tier}`}</span>${stateLine(r, s)}</div>`
    + `<div class="cf-stats"><div class="cf-pw"><b>${C.pwr(d)}</b><span>Power</span></div>`
    + `<div class="cf-lv"><b>${s.owned ? `Level ${lv(id)}` : `★${r.stars}`}</b><span>${s.owned ? 'Unlocked' : 'Squad cost'}</span></div></div>`
    + `<div class="cf-recipe">${r.kind === 'duo' ? sourcesHtml(r, s, true) : (s.owned ? '' : termsHtml(s))}</div>`
    + (what ? `<p class="cf-what">${what} <button class="cf-explain" type="button" data-kind="${r.kind === 'duo' ? 'duos' : 'fusions'}">More</button></p>` : '')
    + forge + (hidden ? '<p class="cf-hint">Swipe for the next one</p>' : '');   // v330 Jack: no "Tap to flip" on a forged card (the flip stays, optional)
}

// ---------------------------------------------------------------- sheets
// v277 THE FORGE SHEET: the cheapest qualifying cards are picked for you (forged.js
// autoSelect); "Change cards" (a quiet link) opens the old swap drawer. One huge
// FORGE button is the confirm: one tap strikes the anvil (forgepress) and forges.
// The ceremony then runs while
// the server spends the cards (its strikes wait for the answer).
let sheetPick = null;    // fusion reagent ids being edited
let swapIdx = -1;
let advOpen = false;
let hold = null;         // the hold-to-forge charge in progress
function sheetEl() { return document.getElementById('forgeSheet'); }
export function closeSheet() {
  const s = sheetEl();
  stopHold();
  if (s) { detachForgedFx(s.querySelector('.fsCard')); s.classList.remove('on', 'charging'); s.style.removeProperty('--charge'); s.innerHTML = ''; }
  removeEventListener('resize', sizeFaces);
  sheetPick = null; swapIdx = -1; advOpen = false;
}
export function openSheet(id) {
  const r = recipe(id);
  if (!r || busy) return;
  sheetPick = null; swapIdx = -1; advOpen = false;
  if (C.audio) C.audio.play('tick', { gain: 0.6 });
  if (C.audio && C.audio.loadSfx) C.audio.loadSfx([...FORGE_SFX, ...ANVIL_SFX, 'forgepress']).catch(() => {});   // the forge moment's hammer, choir + boom
  paintSheet(r);
  sheetEl().classList.add('on');
  addEventListener('resize', sizeFaces);
}
// the cost row: every card face at one size, overlapping only when they cannot fit
function sizeFaces() {
  const row = sheetEl() && sheetEl().querySelector('.fsFaces');
  if (!row) return;
  const n = row.children.length;
  if (!n) return;
  const avail = row.clientWidth, gap = Math.min(12, Math.max(6, Math.min(innerWidth, innerHeight) * 0.012));
  const max = Math.min(170, Math.max(56, Math.min(innerWidth, innerHeight) * 0.14));
  let fw = Math.min(max, (avail - gap * (n - 1)) / n), ov = 0;
  if (fw < 44) { fw = Math.max(34, Math.min(max, (avail * 1.8) / n)); ov = Math.max(0, (n * fw + (n - 1) * gap - avail) / Math.max(1, n - 1)); }
  row.style.setProperty('--fw', `${fw.toFixed(1)}px`);
  row.style.setProperty('--ov', `${ov.toFixed(1)}px`);
}
const QC = ['#5a3b14', '#3a2c6e', '#4a4a5a'];   // the "?" glow per Fusion band (ember / plasma / prismatic)
function heroHtml(r, d, s) {
  const hidden = lockedOf(r, s);
  const pw = `<span class="fsPw"><b>${C.pwr(d)}</b><span>PWR</span><i>· ★${r.stars}</i></span>`;
  if (hidden) {
    const qc = r.kind === 'duo' ? '#5a2d5a' : QC[r.tier >= 17 ? 2 : r.tier >= 9 ? 1 : 0];
    return `<span class="fsCard"><span class="fsQ" style="--qc:${qc}"><b>?</b></span>${pw}<span class="fsHeat"></span></span>`;
  }
  return `<span class="fsCard"><img src="${img(d, 'full')}" alt="">${forgedLayersHtml(d, { size: 'full', autoplay: true })}${s.owned ? '' : pw}<span class="fsHeat"></span></span>`;
}
// v299 recipes spend 40-187 cards: show the first 24 faces and a count for the rest
const FACES_MAX = 24;
function facesHtml(ids, have = null) {
  const more = ids.length > FACES_MAX ? ids.length - (FACES_MAX - 1) : 0;
  return `<div class="fsFaces">${ids.slice(0, more ? FACES_MAX - 1 : FACES_MAX).map((id, i) => {
    const c = C.byId[id];
    return `<span class="fsFace${have && !have[i] ? ' miss' : ''}" title="${esc(c ? c.name : id)}"><img src="${img(c)}" alt="${esc(c ? c.name : id)}" loading="lazy"></span>`;
  }).join('')}${more ? `<span class="fsFace fsMore"><b>+${more}</b></span>` : ''}</div>`;
}
function paintSheet(r) {
  const d = C.byId[r.id];
  const el = sheetEl();
  const duo = r.kind === 'duo';
  const s = duo ? duoState(r) : fusionState(r);
  if (!duo && !sheetPick && s.pick) sheetPick = s.pick.slice();
  const spend = duo ? r.sources : sheetPick;
  const chk = !duo && sheetPick ? checkReq(r.req, sheetPick.map(id => C.byId[id])) : null;
  const valid = !s.owned && (duo ? s.ready || s.blocked : !!(chk && chk.ok));
  const sq = valid ? shortAfter(spend) : null;
  const left = sq ? sq.left : null;
  const short = !!sq && sq.short;                         // econfix: never a forge that ends your battles
  const canForge = valid && !short;
  const name = s.owned || (duo && s.ready) ? d.name : `Unknown ${duo ? 'Duo' : 'Fusion'}`;
  let side;
  if (s.owned) {
    side = `<div class="fsCost"><div class="fsCostH"><b>Unlocked</b><span>Level ${lv(r.id)}</span></div></div>`;
  } else if (canForge) {
    const level = startLevel(spend), lost = xpLost(spend);
    side = `<div class="fsCost"><div class="fsCostH"><b>${duo ? 'Spends both cards' : `Spends ${spend.length} cards`}</b><span>starts at LV ${level}${lost ? ` · ${lost.toLocaleString('en-US')} XP lost` : ''}</span></div>`
      + facesHtml(spend) + (duo ? '' : '<button class="fsAdvL" type="button">Change cards</button>') + '</div>'
      + '<button class="fsGo hot" type="button" aria-label="Forge: press and hold"><span class="fsGoFill"></span><span class="fsGoT"><b>Forge</b><small>Press &amp; hold</small></span></button>';
  } else if (short) {
    side = `<div class="fsCost">${SHORT(left)}${facesHtml(spend)}${duo ? '' : '<button class="fsAdvL" type="button">Change cards</button>'}</div>`
      + '<button class="fsGo" type="button" disabled><span class="fsGoT"><b>Forge</b></span></button>';
  } else {
    const need = duo ? '<div class="fsCostH"><b>Pull both cards</b><span>to forge this Duo</span></div>' + facesHtml(r.sources, s.have)
      : `<div class="fsCostH"><b>Not enough cards yet</b></div><div class="fsReq">${s.terms.map(t => `<span class="${t.ok ? 'ok' : ''}">${esc(t.label)}${t.ok ? ' ✓' : ` ${t.have}/${t.need}`}</span>`).join('')}</div>`;
    side = `<div class="fsCost">${need}</div><button class="fsGo" type="button" disabled><span class="fsGoT"><b>Forge</b></span></button>`;
  }
  let adv = '';
  if (advOpen && !duo && sheetPick && !s.owned) {
    adv = `<div class="fsAdv" role="dialog" aria-label="Change cards"><div class="fsAdvH"><b>Cards to spend · ${sheetPick.length}</b><button type="button" class="fsAdvX">Done</button></div>
      <p>The cheapest cards that fit were picked for you. Tap one to swap it.</p>
      <div class="fsRea">${sheetPick.map((id, i) => {
        const c = C.byId[id];
        return `<button type="button" data-i="${i}" class="${i === swapIdx ? 'sel' : ''}"><img src="${img(c)}" alt=""><i>${esc(c.name)}</i><i>LV ${lv(id)}${spare(id) ? ' · spare' : ` · ${c.rarity}`}</i></button>`;
      }).join('')}</div>`;
    if (swapIdx >= 0) {
      const out = sheetPick[swapIdx];
      const alts = eligibleOwned().filter(c => !sheetPick.includes(c.id)).filter(c => {
        const next = sheetPick.slice(); next[swapIdx] = c.id;
        return checkReq(r.req, next.map(x => C.byId[x])).ok;
      }).slice(0, 40);
      adv += `<div class="fsSwap"><p>Swap <b>${esc(C.byId[out].name)}</b> for:</p>${alts.length ? `<div class="fsRea" data-swap="1">${alts.map(c =>
        `<button type="button" data-alt="${c.id}"><img src="${img(c)}" alt=""><i>${esc(c.name)}</i><i>LV ${lv(c.id)}</i></button>`).join('')}</div>` : '<p>No other card keeps the recipe valid.</p>'}</div>`;
    }
    adv += '</div>';
  }
  el.innerHTML = `<div class="fsIn"><div class="fsTop"><div class="fsTtl"><small>${duo ? 'DUO' : `FUSION · TIER ${r.tier}`}</small><h2>${esc(name)}</h2></div>`
    + `<button class="fsX" type="button" aria-label="Close">✕</button></div>`
    + `<div class="fsStage">${heroHtml(r, d, s)}</div><div class="fsSide">${side}</div></div>${adv}`;
  // the hero card of an unlocked/ready Duo or an unlocked Fusion wears the shared FX layer
  wireForgedVideos(el);
  const heroCard = el.querySelector('.fsCard');
  if (heroCard && heroCard.querySelector('video.ffv') && !advOpen) attachForgedFx(heroCard, d, { z: 60 });
  else detachForgedFx();
  sizeFaces();
  requestAnimationFrame(sizeFaces);
  el.querySelector('.fsX').onclick = () => closeSheet();
  const go = el.querySelector('.fsGo.hot');
  if (go) wireHold(go, r);
  const al = el.querySelector('.fsAdvL');
  if (al) al.onclick = () => { advOpen = true; swapIdx = -1; if (C.audio) C.audio.play('tick', { gain: 0.5 }); paintSheet(r); };
  const ax = el.querySelector('.fsAdvX');
  if (ax) ax.onclick = () => { advOpen = false; swapIdx = -1; paintSheet(r); };
  for (const b of el.querySelectorAll('.fsRea:not([data-swap]) button[data-i]')) {
    b.onclick = () => { swapIdx = swapIdx === +b.dataset.i ? -1 : +b.dataset.i; paintSheet(r); };
  }
  for (const b of el.querySelectorAll('.fsRea[data-swap] button[data-alt]')) {
    b.onclick = () => { sheetPick[swapIdx] = b.dataset.alt; swapIdx = -1; if (C.audio) C.audio.play('tick', { gain: 0.6 }); paintSheet(r); };
  }
}

function stopHold() {
  if (!hold) return;
  cancelAnimationFrame(hold.raf);
  if (hold.cue && hold.cue.gain) { try { hold.cue.gain.setTargetAtTime(0, hold.cue.context.currentTime, 0.04); } catch { /* gone */ } }
  hold = null;
}
// HOLD TO FORGE (restored v333: the "glitch" Jack hit was Android's long-press menu, now blocked app-wide in
// shell.js): ~0.85 s of pressing charges the card (heat, shake, a rising tone); full charge strikes the anvil
// (forgepress) and forges. Letting go early drains it and nudges "press & hold"; a keyboard press forges at once.
const HOLD_MS = 850;
function wireHold(btn, r) {
  const el = sheetEl();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let p = 0, down = false, last = 0, raf = 0, cue = null, ticks = 0;
  const set = (v) => { btn.style.setProperty('--p', v.toFixed(3)); el.style.setProperty('--charge', v.toFixed(3)); };
  const strike = () => {
    if (C.audio) C.audio.play('forgepress', { gain: 1 });
    if (C.haptics) C.haptics.buzz(40);
    doForge(r);
  };
  const tick = (now) => {
    const dt = now - last; last = now;          // real time: a slow device must not stretch the hold
    p = down ? Math.min(1, p + dt / HOLD_MS) : Math.max(0, p - dt / 320);
    set(p);
    if (down && C.haptics && p * 4 > ticks + 1) { ticks++; C.haptics.buzz(8 + ticks * 4); }
    if (down && p >= 1) { down = false; el.classList.remove('charging'); raf = 0; hold = null; strike(); return; }
    if (down || p > 0) { raf = requestAnimationFrame(tick); if (hold) hold.raf = raf; }
    else { raf = 0; el.classList.remove('charging'); }
  };
  btn.addEventListener('pointerdown', (e) => {
    if (busy || down || btn.disabled || (e.button !== undefined && e.button > 0)) return;   // one finger owns the hold
    e.preventDefault();
    try { btn.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
    down = true; ticks = Math.floor(p * 4); last = performance.now();
    btn.classList.remove('nudge');
    if (!reduced) el.classList.add('charging');
    if (C.audio && C.audio.playSynth) cue = C.audio.playSynth('forge-charge', (ctx, out, t0) => buildChargeSfx(ctx, out, t0, { dur: HOLD_MS / 1000 * (1 - p) }));
    hold = { raf, cue };
    if (!raf) raf = requestAnimationFrame(tick);
    hold.raf = raf;
  });
  const up = () => {
    if (!down) return;
    down = false;
    if (cue && cue.gain) { try { cue.gain.setTargetAtTime(0, cue.context.currentTime, 0.04); } catch { /* gone */ } }
    cue = null;
    if (p < 1) { btn.classList.remove('nudge'); void btn.offsetWidth; btn.classList.add('nudge'); }
  };
  btn.addEventListener('pointerup', up);
  btn.addEventListener('pointercancel', up);
  btn.addEventListener('lostpointercapture', up);
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
  // keyboard / assistive activation (no pointer): forge at once
  btn.addEventListener('click', (e) => { if (e.detail === 0 && !busy) strike(); });
}

async function doForge(r) {
  if (busy) return;
  busy = true;
  stopHold();
  const el = sheetEl();
  const spend = r.kind === 'duo' ? r.sources.slice() : sheetPick.slice();
  const faces = [...el.querySelectorAll('.fsFaces .fsFace')];
  // each ingredient flies from where it sat on the sheet
  const reagents = spend.map((id, i) => {
    const b = faces[i] && faces[i].getBoundingClientRect();
    return { url: C.artSrc(C.byId[id], C.V, 'thumb'), rect: b ? { left: b.left, top: b.top, width: b.width, height: b.height } : null };
  });
  const d = C.byId[r.id];
  const pending = Promise.resolve().then(() => C.identity.forge(r.kind, r.id, spend, C.byId))
    .catch((err) => ({ ok: false, code: 'ERROR', error: err && err.message }));
  let res = null, shut = 0, okRes = null, painted = false;
  pending.then((x) => { if (x && x.ok) okRes = x; });
  // v330 FLICKER: the screen under the ceremony (the focus view, the panel) is repainted while the ceremony still covers it
  const repaint = () => { painted = true; if (C.onForged) C.onForged(r, okRes); renderPanel(); paintTabs(); };
  try {
    const cer = Promise.resolve().then(() => runForgeCeremony({
      kind: r.kind, reagents, card: d, artUrl: C.artSrc(d, C.V, 'full'), srcs: duoSrcs(r.id),
      level: startLevel(spend), power: C.pwr(d), pending,
      playSynth: C.audio && C.audio.playSynth, sfxBuffer: C.audio && C.audio.sfxBuffer, loadSfx: C.audio && C.audio.loadSfx,
      haptics: C.haptics, vo: C.audio && C.audio.vo ? (n) => C.audio.vo(n) : null,
      onCovered: closeSheet,                  // the ceremony is opaque: the sheet can go
      onExit: (ok) => { if (ok && okRes && !painted) repaint(); },
    })).catch((err) => { console.warn('[forge] ceremony', err); document.getElementById('forgeCer')?.remove(); });
    shut = setTimeout(closeSheet, 1200);      // fallback only: onCovered closes the sheet as soon as the ceremony is opaque
    res = await pending;
    await cer;
  } finally {
    clearTimeout(shut);
    busy = false;
    closeSheet();
  }
  if (!res || !res.ok) {
    C.toast(res && res.code === 'OFFLINE' ? 'Forging needs a connection.' : res && res.code === 'ALREADY_FORGED' ? 'Already forged.'
      : res && res.code === 'SQUAD_TOO_SMALL' ? `That would leave under ${MIN_SQUAD_FIGHTERS} fighters to battle.`
      : res && res.code === 'CARD_RESERVED' ? 'A card is held for a pending trade.'
      : res && res.code === 'MISSING_CARD' ? 'Some of those cards are no longer in your collection.' : `Forge failed: ${(res && (res.error || res.code)) || 'unknown'}`);
    if (document.getElementById('binder')?.classList.contains('open')) openSheet(r.id);   // only back onto the forge it came from
    return;
  }
  if (!painted) repaint();
}
